import { useMutation, useQuery } from '@apollo/client'
import { Favorite, NoteAlt } from '@mui/icons-material'
import LinkIcon from '@mui/icons-material/Link'
import {
    Autocomplete,
    Box,
    Button,
    IconButton,
    Link,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import { type GridRenderEditCellParams } from '@mui/x-data-grid-pro'
import { RosterSourceStatus, SortIndexField } from '__generated__/graphql'
import { formatDate } from 'date-fns'
import { type FC } from 'react'
import {
    compactListboxProps,
    minimalInputSx,
} from 'recruiter/components/CandidateTableToolbar/minimalFilterStyles'
import ProcessStatusPill from 'recruiter/components/ProcessStatusPill'
import ResumeDownloadButton from 'recruiter/components/ResumeDownloadButton'
import { SourceStatusPill } from 'recruiter/components/SourceStatusPill'
import { type CandidateTableColumnDefinition } from 'recruiter/components/candidateTableColumns'
import CandidateCell from 'recruiter/pages/Research/CustomCells/CandidateCell'
import ColorsAvatarGroup from 'recruiter/pages/Rosters/RosterProfile/CandidatesTableColors/ColorsAvatarGroup'
import ColorsDropdown from 'recruiter/pages/Rosters/RosterProfile/CandidatesTableColors/ColorsDropdown'
import { recruiterRouteConfig } from 'recruiter/routes'
import { GetCandidatesForRosterSql } from 'recruiter/contexts'
import { GetProcessStatusOptions } from 'recruiter/graphql/processStatusOptions.gql'
import { AddCandidatesToRoster } from 'recruiter/graphql/rosters.gql'
import useToast from 'shared/components/Toast/useToast'
import {
    outreachStatusIcons,
    recruitingStatusIcons,
} from 'shared/components/candidateStatusIcons'
import { isCandidateRecruitingStatus } from 'shared/models/candidateRecruitingStatus'
import {
    formatGraphQLError,
    getFullName,
    getInitials,
    sortProcessStatusesBySummaryGroup,
} from 'shared/utils'
import ProcessStatusInlineEditCell from 'recruiter/pages/Rosters/RosterProfile/EditCells/ProcessStatusInlineEditCell'
import { generateRowId } from 'recruiter/components/RosterCandidatesSearchResultsTable'
import { useRosterSearch } from './RosterSearchContext'

const valueGetter = (value: unknown) => value ?? '-'

const FavoritedCell: FC<{ count: number }> = ({ count }) => {
    if (count <= 0) return null
    return (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
            position="relative"
        >
            <Favorite fontSize="medium" color="error" />
            <Typography
                variant="caption"
                position="absolute"
                fontWeight="bold"
                fontSize={10}
                sx={{ color: 'common.white', mt: 0.25 }}
            >
                {count}
            </Typography>
        </Box>
    )
}

const ColorsCell: FC<{ id: string; row: any }> = ({ id, row }) => {
    const { canEdit } = useRosterSearch()
    if (!row.rosterCandidate) return <>-</>
    const colors =
        row.rosterCandidate?.candidateColors
            ?.map(({ rosterColor }: { rosterColor: unknown }) => rosterColor)
            .filter((c: unknown): c is NonNullable<typeof c> => !!c) ?? []
    return (
        <ColorsAvatarGroup
            colors={colors as never}
            id={id.toString()}
            disabled={!canEdit}
        />
    )
}

const ColorsEditCell: FC<GridRenderEditCellParams> = (params) => {
    const { canEdit } = useRosterSearch()
    if (!params.row.rosterCandidate) return <>-</>
    const colors =
        params.row.rosterCandidate?.candidateColors
            ?.map(({ rosterColor }: { rosterColor: unknown }) => rosterColor)
            .filter((c: unknown): c is NonNullable<typeof c> => !!c) ?? []
    return (
        <ColorsDropdown
            colors={colors as never}
            rosterCandidateId={params.row.rosterCandidateId || ''}
            canEdit={canEdit}
            {...params}
        />
    )
}

const InternalNoteCell: FC<{ row: any }> = ({ row }) => {
    const { onOpenNoteDrawer } = useRosterSearch()
    const note = row.rosterCandidate?.internalRecruiterNote?.note ?? ''
    const rosterCandidateId = row.rosterCandidateId

    if (!rosterCandidateId) return <>—</>

    return (
        <Box
            display="flex"
            alignItems="center"
            gap={0.5}
            overflow="hidden"
            width="100%"
        >
            <Typography
                variant="body2"
                sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: 'inherit',
                    flex: 1,
                    minWidth: 0,
                }}
            >
                {note}
            </Typography>
            <IconButton
                size="small"
                onClick={(e) => {
                    e.stopPropagation()
                    onOpenNoteDrawer(rosterCandidateId)
                }}
            >
                <NoteAlt fontSize="small" />
            </IconButton>
        </Box>
    )
}

const ClientNoteCell: FC<{ row: any }> = ({ row }) => {
    const { onOpenNoteDrawer } = useRosterSearch()
    const clientNote = row.rosterCandidate?.latestClientNote
    const note = clientNote?.note ?? ''
    const rating = clientNote?.rating
    const rosterCandidateId = row.rosterCandidateId

    if (!rosterCandidateId) return <>—</>

    const tooltipTitle = clientNote
        ? rating != null
            ? `Rating: ${rating}/5`
            : 'Unrated'
        : ''

    return (
        <Tooltip
            title={tooltipTitle}
            placement="top"
            disableHoverListener={!tooltipTitle}
        >
            <Box
                display="flex"
                alignItems="center"
                gap={0.5}
                overflow="hidden"
                width="100%"
            >
                <Typography
                    variant="body2"
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        lineHeight: 'inherit',
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    {note}
                </Typography>
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation()
                        onOpenNoteDrawer(rosterCandidateId)
                    }}
                >
                    <NoteAlt fontSize="small" />
                </IconButton>
            </Box>
        </Tooltip>
    )
}

const ProcessStatusQuickAdd: FC<{ candidateId: string; rosterId: string }> = ({
    candidateId,
    rosterId,
}) => {
    const toast = useToast()
    const { data: processStatusOptions } = useQuery(GetProcessStatusOptions)

    const [addToRoster] = useMutation(AddCandidatesToRoster, {
        refetchQueries: [GetCandidatesForRosterSql],
        onCompleted: (data) => {
            if (data?.addCandidatesToRoster.success) {
                toast({
                    title: 'Success',
                    message:
                        data.addCandidatesToRoster.message ||
                        'Candidate added to search',
                    severity: 'success',
                })
            } else {
                toast({
                    title: 'Error',
                    message:
                        data?.addCandidatesToRoster.message ||
                        'Error adding candidate to search',
                    severity: 'error',
                })
            }
        },
        onError: (error) => {
            toast({
                title: 'Error',
                message: formatGraphQLError(
                    error,
                    'Error adding candidate to search'
                ),
                severity: 'error',
            })
        },
    })

    return (
        <Autocomplete
            size="small"
            options={sortProcessStatusesBySummaryGroup(
                processStatusOptions?.processStatuses ?? []
            )}
            getOptionLabel={(option) => option.statusLabel}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            groupBy={(option) => option.processStatusSummaryGroup?.label || ''}
            fullWidth
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder="Quick Add"
                    sx={minimalInputSx}
                />
            )}
            renderGroup={(params) => (
                <Box key={params.group}>
                    <Typography
                        sx={{
                            fontWeight: 'bold',
                            fontSize: '0.6875rem',
                            px: 1,
                            pt: 0.5,
                        }}
                    >
                        {params.group}
                    </Typography>
                    {params.children}
                </Box>
            )}
            ListboxProps={compactListboxProps}
            onChange={(_, value) => {
                if (value) {
                    addToRoster({
                        variables: {
                            rosterId,
                            candidateIds: [candidateId],
                            processStatusId: value.id,
                        },
                    })
                }
            }}
            sx={{ minWidth: 120, display: 'flex' }}
        />
    )
}

const ProcessStatusCell: FC<{ row: any }> = ({ row }) => {
    const { rosterId, canEdit, viewMode } = useRosterSearch()

    const processStatus =
        row.rosterCandidate?.candidateProcessStatus?.processStatus

    if (row.rosterCandidateId) {
        return (
            <ProcessStatusInlineEditCell
                rosterId={rosterId}
                canEdit={canEdit}
                currentProcessStatus={processStatus}
                rosterCandidateId={row.rosterCandidateId}
                rowId={generateRowId(row)}
            />
        )
    }

    if (processStatus) {
        return <ProcessStatusPill processStatusId={processStatus.id} />
    }

    if (viewMode === 'all' && canEdit) {
        return (
            <ProcessStatusQuickAdd
                candidateId={row.candidateId}
                rosterId={rosterId}
            />
        )
    }

    return <>-</>
}

export const columnDefinitions = {
    candidate: {
        field: 'candidate',
        headerName: 'Name',
        width: 220,
        sortable: false,
        renderCell: ({ row }) => (
            <CandidateCell
                profileImgKey={
                    row?.profileImageKey || row?.internalProfileImageKey
                }
                candidateId={row.candidateId ?? ''}
                initials={getInitials({
                    firstName: row.firstName,
                    nickname: row.nickname,
                    lastName: row.lastName,
                })}
                name={getFullName({
                    firstName: row.firstName,
                    nickname: row.nickname,
                    lastName: row.lastName,
                })}
                title={row?.currentTitle ?? ''}
                starRating={row?.starRatingAvg}
                hasRemeetNotes={row?.hasRemeetNotes ?? false}
            />
        ),
    },
    colors: {
        field: 'colors',
        headerName: 'Colors',
        width: 65,
        sortable: true,
        sortIndexField: SortIndexField.Colors,
        editable: true,
        renderCell: ({ id, row }) => (
            <ColorsCell id={id.toString()} row={row} />
        ),
        renderEditCell: (params) => <ColorsEditCell {...params} />,
    },
    favorited: {
        field: 'rosterCandidateFavoriteCount',
        headerName: 'Favorited',
        width: 80,
        sortable: false,
        renderCell: ({ row }) => (
            <FavoritedCell count={row.rosterCandidateFavoriteCount ?? 0} />
        ),
    },
    actions: {
        field: 'actions',
        headerName: '',
        width: 100,
        sortable: false,
        renderCell: () => '-',
    },
    internalNote: {
        field: 'internalNote',
        headerName: 'Internal Note',
        flex: 1.5,
        minWidth: 150,
        sortable: true,
        sortIndexField: SortIndexField.RosterInternalNote,
        valueGetter: (_value, row) =>
            row.rosterCandidate?.internalRecruiterNote?.note,
        renderCell: ({ row }) => <InternalNoteCell row={row} />,
    },
    clientNote: {
        field: 'clientNote',
        headerName: 'Client Note',
        flex: 1.5,
        minWidth: 150,
        sortable: true,
        sortIndexField: SortIndexField.ClientNote,
        valueGetter: (_value, row) =>
            row.rosterCandidate?.latestClientNote?.note,
        renderCell: ({ row }) => <ClientNoteCell row={row} />,
    },
    dateAdded: {
        field: 'dateAdded',
        headerName: 'Date Added',
        width: 120,
        sortable: true,
        sortIndexField: SortIndexField.DateOnRoster,
        valueGetter: (_value, row) =>
            row.rosterCandidateCreatedAt
                ? formatDate(row.rosterCandidateCreatedAt, 'MM/dd/yyyy')
                : '-',
    },
    processStatusUpdatedAt: {
        field: 'processStatusUpdatedAt',
        headerName: 'Process Status Updated At',
        width: 180,
        sortable: true,
        sortIndexField: SortIndexField.ProcessStatusUpdatedAt,
        valueGetter: (_value, row) =>
            row.rosterCandidate?.candidateProcessStatus?.createdAt
                ? formatDate(
                      row.rosterCandidate.candidateProcessStatus.createdAt,
                      'MM/dd/yyyy'
                  )
                : '-',
    },
    processStatus: {
        field: 'processStatus',
        headerName: 'Process Status',
        width: 180,
        sortable: true,
        sortIndexField: SortIndexField.ProcessStatus,
        renderCell: ({ row }) => <ProcessStatusCell row={row} />,
    },
    sourceStatus: {
        field: 'sourceStatus',
        headerName: 'Source Status',
        width: 150,
        sortable: false,
        renderCell: ({ row }) => (
            <SourceStatusPill
                status={
                    (row.rosterCandidate?.sourceStatus as RosterSourceStatus) ??
                    undefined
                }
            />
        ),
    },
    status: {
        field: 'status',
        headerName: 'Status',
        width: 75,
        sortable: false,
        renderCell: ({ row }) => {
            const recStatus =
                row.recruitingStatus &&
                isCandidateRecruitingStatus(row.recruitingStatus)
                    ? row.recruitingStatus
                    : 'No Status'
            const RecIcon =
                recruitingStatusIcons[recStatus] ??
                recruitingStatusIcons['No Status']
            const outStatus = row?.outreachStatus ?? 'No Status'
            const OutIcon =
                outreachStatusIcons[outStatus] ??
                outreachStatusIcons['No Status']
            return (
                <Stack direction="row" alignItems="center" gap={0.5}>
                    <RecIcon />
                    <OutIcon />
                </Stack>
            )
        },
    },
    currentCompany: {
        field: 'currentCompany',
        headerName: 'Current Company',
        width: 150,
        sortable: true,
        sortIndexField: SortIndexField.Company,
        renderCell: ({ row }) =>
            row.currentCompany ? (
                <Link
                    href={`/companies/${row.currentCompanyId}`}
                    sx={{ textDecoration: 'none' }}
                >
                    {row.currentCompany}
                </Link>
            ) : (
                '-'
            ),
    },
    currentTitle: {
        field: 'currentTitle',
        headerName: 'Current Title',
        width: 150,
        sortable: true,
        sortIndexField: SortIndexField.CurrentTitle,
        valueGetter,
    },
    currentIndustry: {
        field: 'currentIndustry',
        headerName: 'Current Industry',
        width: 150,
        sortable: true,
        sortIndexField: SortIndexField.CurrentIndustry,
        renderCell: ({ row }) => {
            const industryString =
                row.currentIndustry?.split(';').join(', ') || '-'
            return (
                <Box component="span" title={industryString}>
                    {industryString}
                </Box>
            )
        },
    },
    currentSector: {
        field: 'currentSector',
        headerName: 'Current Sector',
        width: 150,
        sortable: true,
        sortIndexField: SortIndexField.CurrentSector,
        renderCell: ({ row }) => {
            const sectorString = row.currentSector?.split(';').join(', ') || '-'
            return (
                <Box component="span" title={sectorString}>
                    {sectorString}
                </Box>
            )
        },
    },
    currentSectorDescription: {
        field: 'currentSectorDescription',
        headerName: 'Current Sector Description',
        width: 180,
        sortable: true,
        sortIndexField: SortIndexField.SectorDescription,
        valueGetter,
    },
    currentLocation: {
        field: 'currentLocation',
        headerName: 'Current Location',
        width: 150,
        sortable: true,
        sortIndexField: SortIndexField.CurrentLocation,
        valueGetter,
    },
    startDate: {
        field: 'startDate',
        headerName: 'Start Date',
        width: 120,
        sortable: true,
        sortIndexField: SortIndexField.StartDate,
        valueGetter,
    },
    previousCompany: {
        field: 'previousCompany',
        headerName: 'Previous Company',
        width: 150,
        sortable: true,
        sortIndexField: SortIndexField.PreviousCompany,
        valueGetter,
    },
    previousTitle: {
        field: 'previousTitle',
        headerName: 'Previous Title',
        width: 150,
        sortable: false,
        valueGetter,
    },
    previousIndustry: {
        field: 'previousIndustry',
        headerName: 'Previous Industry',
        width: 150,
        sortable: true,
        sortIndexField: SortIndexField.PreviousIndustry,
        valueGetter,
    },
    previousSector: {
        field: 'previousSector',
        headerName: 'Previous Sector',
        width: 150,
        sortable: true,
        sortIndexField: SortIndexField.PreviousSector,
        valueGetter,
    },
    previousStartDate: {
        field: 'previousStartDate',
        headerName: 'Previous Start Date',
        width: 150,
        sortable: false,
        valueGetter,
    },
    gender: {
        field: 'gender',
        headerName: 'Gender',
        width: 100,
        sortable: true,
        sortIndexField: SortIndexField.Gender,
        valueGetter,
    },
    ethnicity: {
        field: 'ethnicity',
        headerName: 'Race/Ethnicity',
        width: 130,
        sortable: true,
        sortIndexField: SortIndexField.Ethnicity,
        valueGetter,
    },
    visaStatus: {
        field: 'visaStatus',
        headerName: 'Visa Status',
        width: 120,
        sortable: true,
        sortIndexField: SortIndexField.VisaStatus,
        valueGetter,
    },
    cpiPlaced: {
        field: 'cpiPlaced',
        headerName: 'CPI Placed (Pre & Post)',
        width: 180,
        sortable: true,
        sortIndexField: SortIndexField.CpiAttribute,
        valueGetter: (_value, row) => row?.cpiAttribute ?? '-',
    },
    undergradEducation: {
        field: 'undergradEducation',
        headerName: 'Undergrad Education',
        width: 180,
        sortable: true,
        sortIndexField: SortIndexField.UndergradCollege,
        valueGetter: (_value, row) => row.undergradEducation || '-',
    },
    undergradYear: {
        field: 'undergradYear',
        headerName: 'Undergrad Year',
        width: 130,
        sortable: true,
        sortIndexField: SortIndexField.UndergradYear,
        valueGetter,
    },
    undergradGpa: {
        field: 'undergradGpa',
        headerName: 'Undergrad GPA',
        width: 120,
        sortable: true,
        sortIndexField: SortIndexField.UndergradGpa,
        valueGetter,
    },
    mbaEducation: {
        field: 'mbaEducation',
        headerName: 'MBA Education',
        width: 150,
        sortable: false,
        valueGetter,
    },
    mbaYear: {
        field: 'mbaYear',
        headerName: 'MBA Year',
        width: 110,
        sortable: false,
        valueGetter,
    },
    mbaTiering: {
        field: 'mbaTiering',
        headerName: 'MBA Tiering',
        width: 120,
        sortable: false,
        valueGetter,
    },
    sat: {
        field: 'sat',
        headerName: 'SAT',
        width: 100,
        sortable: true,
        sortIndexField: SortIndexField.Sat,
        valueGetter,
    },
    act: {
        field: 'act',
        headerName: 'ACT',
        width: 100,
        sortable: true,
        sortIndexField: SortIndexField.Act,
        valueGetter,
    },
    gmat: {
        field: 'gmat',
        headerName: 'GMAT',
        width: 100,
        sortable: true,
        sortIndexField: SortIndexField.Gmat,
        valueGetter,
    },
    ccatRawScore: {
        field: 'ccatRawScore',
        headerName: 'CCAT Raw Score',
        width: 140,
        sortable: false,
        valueGetter,
    },
    ccatPercentile: {
        field: 'ccatPercentile',
        headerName: 'CCAT Percentile',
        width: 140,
        sortable: false,
        valueGetter,
    },
    hometown: {
        field: 'hometown',
        headerName: 'Hometown',
        width: 150,
        sortable: true,
        sortIndexField: SortIndexField.Hometown,
        valueGetter,
    },
    analystClassYear: {
        field: 'analystClassYear',
        headerName: 'Analyst Class Year',
        width: 150,
        sortable: true,
        sortIndexField: SortIndexField.AnalystClassYear,
        valueGetter,
    },
    preAdmitAttribute: {
        field: 'preAdmitAttribute',
        headerName: 'Pre-Admit Attribute',
        width: 150,
        sortable: true,
        sortIndexField: SortIndexField.PreAdmitAttribute,
        valueGetter: (_value, row) => (row?.preAdmitAttribute ? 'Yes' : 'No'),
    },
    referenceAttribute: {
        field: 'referenceAttribute',
        headerName: 'Reference Attribute',
        width: 150,
        sortable: true,
        sortIndexField: SortIndexField.ReferenceAttribute,
        valueGetter,
    },
    starRatingAvg: {
        field: 'starRatingAvg',
        headerName: 'Star Rating (avg)',
        width: 140,
        sortable: true,
        sortIndexField: SortIndexField.StarRating,
        valueGetter,
    },
    starRatingMostRecentScreen: {
        field: 'starRatingMostRecentScreen',
        headerName: 'Star Rating (most recent screen)',
        width: 220,
        sortable: true,
        sortIndexField: SortIndexField.StarRatingMostRecentScreen,
        valueGetter,
    },
    starRatingReference: {
        field: 'starRatingReference',
        headerName: 'Star Rating (reference)',
        width: 180,
        sortable: true,
        sortIndexField: SortIndexField.StarRatingReference,
        valueGetter,
    },
    rosterInterestFromBlast: {
        field: 'rosterInterestFromBlast',
        headerName: 'Is Interested (from blast)',
        width: 180,
        sortable: false,
        valueGetter: (value: boolean | null | undefined) =>
            value === true ? 'Yes' : value === false ? 'No' : '-',
    },
    rosterNotInterestedReasonFromBlast: {
        field: 'rosterNotInterestedReasonFromBlast',
        headerName: 'Not Interested Reason (from blast)',
        width: 230,
        sortable: false,
        valueGetter,
    },
    rosterCommentsFromBlast: {
        field: 'rosterCommentsFromBlast',
        headerName: 'Comments (from blast)',
        width: 180,
        sortable: false,
        valueGetter,
    },
    rosterLocationFromBlast: {
        field: 'rosterLocationFromBlast',
        headerName: 'Roster Location (from blast)',
        width: 200,
        sortable: false,
        valueGetter: (_value, row) => {
            const prefs =
                row?.rosterCandidate?.mostRecentJobBoardResponse?.officePrefs
            if (!prefs || prefs.length === 0) return '-'
            return (
                prefs
                    .map((op) => op.companyOffice?.officeLocation?.value)
                    .filter(Boolean)
                    .join(', ') || '-'
            )
        },
    },
    rosterSectorFromBlast: {
        field: 'rosterSectorFromBlast',
        headerName: 'Roster Sector (from blast)',
        width: 200,
        sortable: false,
        valueGetter: (_value, row) => {
            const prefs =
                row?.rosterCandidate?.mostRecentJobBoardResponse?.sectorPrefs
            if (!prefs || prefs.length === 0) return '-'
            return (
                prefs
                    .map((sp) => sp.sector?.value)
                    .filter(Boolean)
                    .join(', ') || '-'
            )
        },
    },
    recruitingIndustryPref1: {
        field: 'recruitingIndustryPref1',
        headerName: 'Recruiting Industry Pref #1',
        width: 200,
        sortable: true,
        sortIndexField: SortIndexField.RecruitingIndustryPref_1,
        valueGetter,
    },
    recruitingIndustryPref2: {
        field: 'recruitingIndustryPref2',
        headerName: 'Recruiting Industry Pref #2',
        width: 200,
        sortable: true,
        sortIndexField: SortIndexField.RecruitingIndustryPref_2,
        valueGetter,
    },
    recruitingSectorPref1: {
        field: 'recruitingSectorPref1',
        headerName: 'Recruiting Sector Pref #1',
        width: 200,
        sortable: true,
        sortIndexField: SortIndexField.RecruitingSectorPref_1,
        valueGetter,
    },
    recruitingSectorPref2: {
        field: 'recruitingSectorPref2',
        headerName: 'Recruiting Sector Pref #2',
        width: 200,
        sortable: true,
        sortIndexField: SortIndexField.RecruitingSectorPref_2,
        valueGetter,
    },
    recruitingSectorPref3: {
        field: 'recruitingSectorPref3',
        headerName: 'Recruiting Sector Pref #3',
        width: 200,
        sortable: false,
        valueGetter,
    },
    recruitingGeoPref1: {
        field: 'recruitingGeoPref1',
        headerName: 'Recruiting Geo Pref #1',
        width: 180,
        sortable: true,
        sortIndexField: SortIndexField.RecruitingGeoPref_1,
        valueGetter: (_value, row) => row.recruitingGeoLocationPref1 || '-',
    },
    recruitingGeoPref2: {
        field: 'recruitingGeoPref2',
        headerName: 'Recruiting Geo Pref #2',
        width: 180,
        sortable: true,
        sortIndexField: SortIndexField.RecruitingGeoPref_2,
        valueGetter: (_value, row) => row.recruitingGeoLocationPref2 || '-',
    },
    recruitingGeoPref3: {
        field: 'recruitingGeoPref3',
        headerName: 'Recruiting Geo Pref #3',
        width: 180,
        sortable: false,
        valueGetter: (_value, row) => row.recruitingGeoLocationPref3 || '-',
    },
    email: {
        field: 'email',
        headerName: 'Email Address',
        width: 200,
        sortable: true,
        sortIndexField: SortIndexField.Email,
        valueGetter,
    },
    preferredEmail: {
        field: 'preferredEmail',
        headerName: 'Preferred Email',
        width: 200,
        sortable: true,
        sortIndexField: SortIndexField.PreferredEmail,
        valueGetter,
    },
    currentWorkEmail: {
        field: 'currentWorkEmail',
        headerName: 'Work Email',
        width: 200,
        sortable: true,
        sortIndexField: SortIndexField.WorkEmail,
        valueGetter,
    },
    phone: {
        field: 'phone',
        headerName: 'Personal Phone',
        width: 150,
        sortable: false,
        valueGetter,
    },
    publicBio: {
        field: 'publicBio',
        headerName: 'Public Bio',
        width: 200,
        sortable: false,
        valueGetter,
    },
    linkedinUrl: {
        field: 'linkedinUrl',
        headerName: 'LinkedIn URL',
        width: 150,
        sortable: false,
        renderCell: ({ row }) =>
            row.linkedinUrl ? (
                <Link href={`//${row.linkedinUrl}`}>{row.linkedinUrl}</Link>
            ) : (
                '-'
            ),
    },
    resumeUrl: {
        field: 'resumeUrl',
        headerName: 'Resume/CV File',
        width: 150,
        sortable: false,
        renderCell: ({ row }) =>
            row.candidateDocumentId ? (
                <ResumeDownloadButton
                    documentId={row.candidateDocumentId}
                    documentKey={row.candidateDocumentKey}
                />
            ) : (
                '-'
            ),
    },
    resumeUpdatedDate: {
        field: 'resumeUpdatedDate',
        headerName: 'Resume Updated Date',
        width: 170,
        sortable: true,
        sortIndexField: SortIndexField.ResumeUpdated,
        valueGetter: (value) =>
            value ? formatDate(new Date(value as string), 'MM/dd/yyyy') : '-',
    },
    candidateProfileUrl: {
        field: 'candidateProfileUrl',
        headerName: 'Candidate Profile URL',
        width: 180,
        sortable: false,
        renderCell: ({ row }) => (
            <Button
                href={`${recruiterRouteConfig.candidates.path}/${row.candidateId}`}
                aria-label="View Candidate Profile"
                variant="text"
                size="small"
            >
                <LinkIcon />
            </Button>
        ),
    },
} as const satisfies Record<string, CandidateTableColumnDefinition>

export type ColumnKey = keyof typeof columnDefinitions
